import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { blogDetailUrl, UIDate, useBlogSearch, useSocialCoachSession } from "@/utils";
import { GetBlogsFilters } from "@/services/bedrock";
import LibraryCard from "@/components/Cells/LibraryCard";
import { Svg } from "@/assets";
import { Badge, Spinner, Pagination } from "@/components/ui";
import { EmptyView } from "@/components/EmptyView";
import { toast } from "sonner";
import { BlogStatusBadge } from "./BlogStatusBadge";
import { BlogsSearch } from "./BlogsSearch";

export const BlogsList: React.FC = () => {
  const history = useHistory();
  const { isCoach, isPlayer } = useSocialCoachSession();
  const [page, setPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState<GetBlogsFilters>({});

  const { searchBlogs } = useBlogSearch();

  const {
    blogs: blogsPage,
    isFetching: loading,
    isError,
  } = searchBlogs({
    ...searchFilters,
    page,
    perPage: 21,
    sort: "created_date",
    order: "desc",
    extraFields: ["totalLinkClicks"],
  });

  const blogs = blogsPage?.content ?? [];
  const totalPages = blogsPage?.pagination?.totalPages ?? 0;

  useEffect(() => {
    if (isError) toast.error("Failed to fetch blogs");
  }, [isError]);

  const handleSearchChange = (filters: GetBlogsFilters) => {
    setSearchFilters(filters);
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="text-xl font-semibold text-foreground">{isCoach ? "Player " : ""}Blogs</div>

      <div className="text-sm text-muted-foreground">
        Blogs are written from a Video Page or Video Project transcript. Open one and choose Generate Blog to create it.
      </div>

      <BlogsSearch onSearchChange={handleSearchChange} />

      <div className="flex flex-col">
        <div className="flex flex-wrap gap-x-5">
          {blogs.map(blog => (
            <LibraryCard
              key={`blog-${blog.id}`}
              title={blog.title || <i>Untitled blog</i>}
              thumbnail={blog.thumbnailUrl ?? Svg.PromptPlaceholder}
              dateLabel={UIDate(blog.publishedAt || blog.createdDate || new Date().toLocaleDateString())}
              className="h-65 min-h-65"
              disabled={loading}
              extra={
                <div className="flex flex-wrap items-center gap-1">
                  <BlogStatusBadge status={blog.status} />
                  {!!blog.totalLinkClicks && <Badge variant="outline">{blog.totalLinkClicks} clicks</Badge>}
                </div>
              }
              onClick={() => history.push(blogDetailUrl(!!isPlayer, blog.id))}
            />
          ))}
        </div>
        {blogs.length === 0 &&
          (loading ? (
            <Spinner className="mx-auto my-10 size-10" />
          ) : (
            <EmptyView text="No Blogs found." customIconImage={Svg.Library} />
          ))}
      </div>

      {totalPages > 1 && (
        <div>
          <Pagination page={page} totalPages={totalPages} boundaryRange={3} onPageChange={setPage} disabled={loading} />
        </div>
      )}
    </div>
  );
};
